import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Landmark,
  Users,
  FileText,
  CheckCircle2,
  TrendingUp,
  Wallet,
  IndianRupee,
  Percent,
  RefreshCw,
  AlertTriangle,
  Building2,
  ChevronRight,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../context/PermissionContext";
import { dsaApi } from "../services/api";

const { RangePicker } = DatePicker;

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

// Value na mile to "—" — zero maan lena galat picture dega
const formatINROrDash = (val) => (val === null || val === undefined ? "—" : formatINR(val));
const formatCountOrDash = (val) =>
  val === null || val === undefined ? "—" : new Intl.NumberFormat("en-IN").format(val);
const formatPctOrDash = (val) => (val === null || val === undefined ? "—" : `${Number(val).toFixed(1)}%`);

const num = (val) => {
  if (val === null || val === undefined || val === "") return null;
  const n = typeof val === "string" ? Number(val.replace(/[^0-9.-]/g, "")) : Number(val);
  return Number.isFinite(n) ? n : null;
};

// Backend key naming alag ho sakti hai — pehli maujood key utha lo
const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
  }
  return undefined;
};

const toArray = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.content)) return res.content;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.content)) return res.data.content;
  if (Array.isArray(res?.lenders)) return res.lenders;
  if (Array.isArray(res?.performance)) return res.performance;
  return [];
};

const unwrap = (res) => res?.data ?? res ?? null;

// Funnel ke stages — jo API bheje wahi dikhao, missing stage fake nahi karenge
const FUNNEL_STAGES = [
  {
    key: "leads",
    label: "Leads",
    keys: ["leads", "totalLeads", "leadCount", "leadsCount"],
    Icon: Users,
    color: "#3b82f6",
    tint: "bg-blue-50 text-blue-600",
  },
  {
    key: "loggedIn",
    label: "Files Logged In",
    keys: ["filesLoggedIn", "loggedIn", "logins", "loginCount", "filesLogged", "submitted"],
    Icon: FileText,
    color: "#6366f1",
    tint: "bg-indigo-50 text-indigo-600",
  },
  {
    key: "sanctioned",
    label: "Sanctioned",
    keys: ["sanctioned", "sanctionedCount", "approved", "totalSanctioned"],
    Icon: CheckCircle2,
    color: "#10b981",
    tint: "bg-emerald-50 text-emerald-600",
  },
  {
    key: "disbursed",
    label: "Disbursed",
    keys: ["disbursed", "disbursedCount", "totalDisbursed", "disbursedFiles"],
    Icon: TrendingUp,
    color: "#8b5cf6",
    tint: "bg-purple-50 text-purple-600",
  },
];

// Lender performance row ko fixed shape me laao
const normalizeLenderRow = (raw = {}) => ({
  lenderName:
    pick(raw, ["lenderName", "name", "bankName", "partnerName"]) ||
    pick(raw.lender || {}, ["name", "lenderName", "bankName"]),
  lenderId: pick(raw, ["lenderId", "id"]) || pick(raw.lender || {}, ["id", "lenderId"]),
  filesLogged: num(pick(raw, ["filesLogged", "loggedIn", "filesLoggedIn", "totalFiles", "submissions"])),
  sanctioned: num(pick(raw, ["sanctioned", "sanctionedCount", "approved"])),
  rejected: num(pick(raw, ["rejected", "rejectedCount", "declined"])),
  disbursed: num(pick(raw, ["disbursed", "disbursedCount", "disbursedFiles"])),
  disbursedAmount: num(
    pick(raw, ["disbursedAmount", "totalDisbursedAmount", "disbursalAmount", "disbursementAmount"])
  ),
  sanctionRatio: num(pick(raw, ["sanctionRatio", "sanctionRatioPercentage", "sanctionPercentage", "sanctionRate"])),
  avgTat: num(pick(raw, ["avgTat", "averageTat", "avgTatDays", "averageTatDays", "tatDays"])),
});

export default function DsaDashboard() {
  const { currentTheme } = useTheme();
  const primaryColor = currentTheme?.color || "#2e856e";
  const { hasAny } = usePermissions();

  // Default — current month
  const [dateRange, setDateRange] = useState([dayjs().startOf("month"), dayjs().endOf("month")]);

  const [loading, setLoading] = useState(true);
  const [funnelRaw, setFunnelRaw] = useState(null);
  const [funnelError, setFunnelError] = useState(null);
  const [lenderRows, setLenderRows] = useState([]);
  const [lenderError, setLenderError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setFunnelError(null);
    setLenderError(null);

    const params = {};
    if (dateRange && dateRange[0] && dateRange[1]) {
      params.startDate = dateRange[0].format("YYYY-MM-DD");
      params.endDate = dateRange[1].format("YYYY-MM-DD");
    }

    // Ek section fail ho to doosra to dikh sake — isliye allSettled
    const [funnelRes, perfRes] = await Promise.allSettled([
      dsaApi.getDsaFunnel(params),
      dsaApi.getLenderPerformance(params),
    ]);

    if (funnelRes.status === "fulfilled") {
      setFunnelRaw(unwrap(funnelRes.value));
    } else {
      console.error("DSA funnel load failed:", funnelRes.reason);
      setFunnelError(funnelRes.reason?.message || "Failed to load DSA funnel");
      setFunnelRaw(null);
    }

    if (perfRes.status === "fulfilled") {
      setLenderRows(toArray(perfRes.value).map(normalizeLenderRow));
    } else {
      console.error("DSA lender performance load failed:", perfRes.reason);
      setLenderError(perfRes.reason?.message || "Failed to load lender performance");
      setLenderRows([]);
    }

    // API fail hui to hi message — success par koi toast nahi
    if (funnelRes.status === "rejected" && perfRes.status === "rejected") {
      message.error(funnelRes.reason?.message || "Failed to load DSA dashboard");
    } else if (funnelRes.status === "rejected" || perfRes.status === "rejected") {
      message.error("Some dashboard sections failed to load");
    }

    setLoading(false);
  }, [dateRange]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // ── Funnel: sirf wahi stages jo API se aaye ──
  const funnel = useMemo(() => {
    if (!funnelRaw) return [];
    const src = funnelRaw.funnel || funnelRaw.stages || funnelRaw;
    if (!src || typeof src !== "object") return [];

    // Backend agar already [{stage, count}] bhejta hai to usi ko use karo
    if (Array.isArray(src)) {
      return src
        .map((s) => ({
          label: pick(s, ["stage", "label", "name"]) || "—",
          value: num(pick(s, ["count", "value", "total"])),
          color: "#2e856e",
          tint: "bg-gray-50 text-gray-600",
          Icon: FileText,
        }))
        .filter((s) => s.value !== null);
    }

    return FUNNEL_STAGES.map((stage) => ({
      label: stage.label,
      value: num(pick(src, stage.keys)),
      color: stage.color,
      tint: stage.tint,
      Icon: stage.Icon,
    })).filter((s) => s.value !== null);
  }, [funnelRaw]);

  // ── Stat cards ── (jo API de wahi; warna derive; warna "—")
  const stats = useMemo(() => {
    const src = funnelRaw || {};
    const totalDisbursedAmount = num(
      pick(src, ["totalDisbursedAmount", "disbursedAmount", "totalDisbursal", "disbursementAmount"])
    );
    const commissionEarned = num(
      pick(src, ["commissionEarned", "totalCommission", "commissionAmount", "earnedCommission"])
    );

    const disbursedCount = num(pick(src, ["disbursed", "disbursedCount", "totalDisbursed", "disbursedFiles"]));
    let avgTicket = num(pick(src, ["averageTicketSize", "avgTicketSize", "averageTicket"]));
    if (avgTicket === null && totalDisbursedAmount !== null && disbursedCount) {
      avgTicket = totalDisbursedAmount / disbursedCount;
    }

    const loggedIn = num(pick(src, ["filesLoggedIn", "loggedIn", "logins", "loginCount", "filesLogged", "submitted"]));
    const sanctioned = num(pick(src, ["sanctioned", "sanctionedCount", "approved", "totalSanctioned"]));
    let sanctionRatio = num(pick(src, ["sanctionRatio", "sanctionRatioPercentage", "sanctionPercentage"]));
    if (sanctionRatio === null && sanctioned !== null && loggedIn) {
      sanctionRatio = (sanctioned / loggedIn) * 100;
    }

    return { totalDisbursedAmount, commissionEarned, avgTicket, sanctionRatio };
  }, [funnelRaw]);

  // Bar chart data — sirf un lenders ka jinke paas disbursed amount hai
  const chartData = useMemo(
    () =>
      lenderRows
        .filter((r) => r.disbursedAmount !== null && r.disbursedAmount > 0)
        .map((r) => ({ lender: r.lenderName || "—", disbursedAmount: r.disbursedAmount }))
        .sort((a, b) => b.disbursedAmount - a.disbursedAmount),
    [lenderRows]
  );

  const canView = hasAny(["DSA_DASHBOARD_VIEW", "DSA_COMMISSION_VIEW", "DSA_PIPELINE_VIEW"]);

  return (
    <div className="space-y-6 pb-12">
      {/* ── Page Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
            style={{ backgroundColor: primaryColor }}
          >
            <Landmark className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">DSA / Loan Sourcing Dashboard</h1>
            <p className="text-xs md:text-sm text-gray-500">
              Files sourced for partner banks & NBFCs — login to disbursal to commission
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format="DD/MM/YYYY"
            className="rounded-xl border-gray-300 py-2 shadow-sm font-medium"
          />

          <button
            onClick={fetchDashboard}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow-md transition-all hover:brightness-105 active:scale-95 disabled:opacity-60"
            style={{ backgroundColor: primaryColor }}
            title="Refresh Data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {!canView && (
        <div className="bg-white p-4 rounded-2xl border border-amber-200 shadow-sm flex items-center gap-2 text-sm text-amber-700">
          <AlertTriangle className="w-4 h-4" />
          <span>You may not have DSA dashboard access — data below will load only if the server allows it.</span>
        </div>
      )}

      {/* ── Sourcing Funnel ── */}
      <div className="bg-white p-6 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-bold text-gray-900">Sourcing Funnel</h2>
            <p className="text-xs text-gray-500">Leads → Files Logged In → Sanctioned → Disbursed</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div
              className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: primaryColor, borderTopColor: "transparent" }}
            />
          </div>
        ) : funnelError ? (
          <div className="flex flex-col items-center text-center gap-3 py-12 px-6">
            <AlertTriangle className="w-10 h-10 text-amber-500" />
            <h3 className="text-base font-bold text-gray-900">Could not load the DSA funnel</h3>
            <p className="text-sm text-gray-500 max-w-md">{funnelError}</p>
            <button
              onClick={fetchDashboard}
              className="mt-1 px-4 py-2 rounded-xl text-sm font-semibold text-white flex items-center gap-2"
              style={{ backgroundColor: primaryColor }}
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        ) : funnel.length === 0 ? (
          <div className="flex flex-col items-center text-center gap-2 py-12 text-gray-400">
            <FileText className="w-10 h-10 opacity-30" />
            <p className="font-semibold">No funnel data for this period</p>
            <button
              onClick={fetchDashboard}
              className="mt-2 px-3 py-1.5 rounded-xl text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 inline-flex items-center gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-3">
            {funnel.map((stage, idx) => {
              const prev = idx > 0 ? funnel[idx - 1] : null;
              // Conversion sirf tab jab pichla stage > 0 ho
              const conversion = prev && prev.value > 0 ? (stage.value / prev.value) * 100 : null;
              const StageIcon = stage.Icon || FileText;
              return (
                <React.Fragment key={`${stage.label}-${idx}`}>
                  {idx > 0 && (
                    <div className="flex lg:flex-col items-center justify-center gap-1 px-1">
                      <ChevronRight className="w-4 h-4 text-gray-300 rotate-90 lg:rotate-0" />
                      <span className="text-[11px] font-bold text-gray-500 whitespace-nowrap">
                        {conversion === null ? "—" : `${conversion.toFixed(1)}%`}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 p-4 rounded-2xl border border-gray-200/80 bg-gray-50/40">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-500">{stage.label}</span>
                      <div className={`p-2 rounded-xl ${stage.tint}`}>
                        <StageIcon className="w-4 h-4" />
                      </div>
                    </div>
                    <div className="text-2xl font-black text-gray-900">{formatCountOrDash(stage.value)}</div>
                    <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          backgroundColor: stage.color,
                          width:
                            funnel[0]?.value > 0
                              ? `${Math.min((stage.value / funnel[0].value) * 100, 100)}%`
                              : "0%",
                        }}
                      />
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Total Disbursed</span>
            <div className="p-2 bg-purple-50 text-purple-600 rounded-xl">
              <Wallet className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900">
            {loading ? "…" : formatINROrDash(stats.totalDisbursedAmount)}
          </div>
          <p className="text-xs text-gray-500 mt-1">Amount disbursed by lenders</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">Commission Earned</span>
            <div className="p-2 bg-[#2e856e]/10 text-[#2e856e] rounded-xl">
              <IndianRupee className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-emerald-700">
            {loading ? "…" : formatINROrDash(stats.commissionEarned)}
          </div>
          <p className="text-xs text-gray-500 mt-1">Payout booked for the period</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Avg Ticket Size</span>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <FileText className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900">{loading ? "…" : formatINROrDash(stats.avgTicket)}</div>
          <p className="text-xs text-gray-500 mt-1">Per disbursed file</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-600">Sanction Ratio</span>
            <div className="p-2 bg-amber-50 text-amber-600 rounded-xl">
              <Percent className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-amber-700">
            {loading ? "…" : formatPctOrDash(stats.sanctionRatio)}
          </div>
          <p className="text-xs text-gray-500 mt-1">Sanctioned vs files logged in</p>
        </div>
      </div>

      {/* ── Disbursed Amount by Lender (chart sirf tab jab data ho) ── */}
      {!loading && !lenderError && chartData.length > 0 && (
        <div className="bg-white p-6 rounded-2xl border border-[#e5e7eb] shadow-sm">
          <div className="mb-4">
            <h2 className="text-base font-bold text-gray-900">Disbursed Amount by Lender</h2>
            <p className="text-xs text-gray-500">Partner-wise disbursal volume in INR (₹)</p>
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="lender" tick={{ fill: "#6b7280", fontSize: 11 }} interval={0} />
                <YAxis
                  tickFormatter={(val) => `₹${val / 100000}L`}
                  tick={{ fill: "#6b7280", fontSize: 11 }}
                />
                <Tooltip
                  formatter={(value) => [formatINR(value), "Disbursed"]}
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                />
                <Bar dataKey="disbursedAmount" fill={primaryColor} name="Disbursed Amount" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Lender Performance Table ── */}
      <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden">
        <div className="p-6 pb-3">
          <h2 className="text-base font-bold text-gray-900">Lender Performance</h2>
          <p className="text-xs text-gray-500">Login, sanction and disbursal health of each partner</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div
              className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: primaryColor, borderTopColor: "transparent" }}
            />
          </div>
        ) : lenderError ? (
          <div className="flex flex-col items-center text-center gap-3 py-14 px-6">
            <AlertTriangle className="w-10 h-10 text-amber-500" />
            <h3 className="text-base font-bold text-gray-900">Could not load lender performance</h3>
            <p className="text-sm text-gray-500 max-w-md">{lenderError}</p>
            <button
              onClick={fetchDashboard}
              className="mt-1 px-4 py-2 rounded-xl text-sm font-semibold text-white flex items-center gap-2"
              style={{ backgroundColor: primaryColor }}
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                  <th className="py-4 px-4">Lender</th>
                  <th className="py-4 px-4 text-center">Files Logged</th>
                  <th className="py-4 px-4 text-center">Sanctioned</th>
                  <th className="py-4 px-4 text-center">Rejected</th>
                  <th className="py-4 px-4 text-center">Disbursed</th>
                  <th className="py-4 px-4 text-right">Disbursed Amount</th>
                  <th className="py-4 px-4 text-center">Sanction Ratio %</th>
                  <th className="py-4 px-4 text-center">Avg TAT (days)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lenderRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-gray-400">
                      <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="font-semibold">No lender performance data for this period</p>
                      <p className="text-xs text-gray-400 mt-1">Try a different date range, or refresh</p>
                      <button
                        onClick={fetchDashboard}
                        className="mt-3 px-3 py-1.5 rounded-xl text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 inline-flex items-center gap-1.5"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Retry
                      </button>
                    </td>
                  </tr>
                ) : (
                  lenderRows.map((row, idx) => {
                    // Ratio API na de to logged/sanctioned se nikal lo
                    const ratio =
                      row.sanctionRatio !== null
                        ? row.sanctionRatio
                        : row.sanctioned !== null && row.filesLogged
                        ? (row.sanctioned / row.filesLogged) * 100
                        : null;
                    return (
                      <tr key={row.lenderId ?? `${row.lenderName}-${idx}`} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-2 font-semibold text-gray-900">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span>{row.lenderName || "—"}</span>
                          </div>
                        </td>
                        <td className="py-3.5 px-4 text-center font-medium text-gray-700">
                          {formatCountOrDash(row.filesLogged)}
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold text-emerald-700">
                          {formatCountOrDash(row.sanctioned)}
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold text-red-600">
                          {formatCountOrDash(row.rejected)}
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold text-purple-700">
                          {formatCountOrDash(row.disbursed)}
                        </td>
                        <td className="py-3.5 px-4 text-right font-black text-gray-900">
                          {formatINROrDash(row.disbursedAmount)}
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          {ratio === null ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <span
                              className={`inline-flex px-2 py-1 rounded-full text-xs font-bold ${
                                ratio >= 60
                                  ? "bg-emerald-50 text-emerald-700"
                                  : ratio >= 30
                                  ? "bg-amber-50 text-amber-700"
                                  : "bg-red-50 text-red-700"
                              }`}
                            >
                              {ratio.toFixed(1)}%
                            </span>
                          )}
                        </td>
                        <td className="py-3.5 px-4 text-center font-semibold text-gray-700">
                          {row.avgTat === null ? "—" : row.avgTat}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
